/**
 * Bulk Properties Panel Component
 *
 * Displays a form for editing common properties across multiple selected objects
 * of the same type. Shows mixed value indicators for fields with different values.
 */

import { useMemo, useCallback, useState } from 'react';
import { useIDFStore } from '../stores/idfStore';
import { useIDDStore } from '../stores/iddStore';
import type { IDFObject } from '../parser/types';
import type { IDDField } from '../idd/types';
import { getFieldEditorType } from './types';

/**
 * Represents the aggregated state of a field across multiple objects
 */
interface AggregatedFieldState {
  fieldIndex: number;
  fieldInfo: IDDField;
  /** The common value if all objects have the same value, undefined otherwise */
  commonValue: string | undefined;
  /** Whether all objects have the same value for this field */
  isMixed: boolean;
  /** All unique values found across objects */
  uniqueValues: string[];
  /** Number of objects with each value */
  valueCounts: Map<string, number>;
}

/**
 * Props for the BulkPropertiesPanel component
 */
interface BulkPropertiesPanelProps {
  selectedObjectIds: string[];
}

/**
 * Icon for mixed values indicator
 */
function MixedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </svg>
  );
}

/**
 * Field group separator
 */
function FieldGroupSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-2">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

/**
 * Bulk field editor for a single field across multiple objects
 */
function BulkFieldEditor({
  aggregatedField,
  objectIds,
  referencedObjects,
}: {
  aggregatedField: AggregatedFieldState;
  objectIds: string[];
  referencedObjects: IDFObject[];
}) {
  const { fieldInfo, fieldIndex, commonValue, isMixed, uniqueValues, valueCounts } =
    aggregatedField;
  const updateFieldForMultipleObjects = useIDFStore(
    (state) => state.updateFieldForMultipleObjects
  );
  const [showMixedDetails, setShowMixedDetails] = useState(false);
  const [editValue, setEditValue] = useState<string | null>(null);

  const handleChange = useCallback(
    (value: string) => {
      setEditValue(value);
      updateFieldForMultipleObjects(objectIds, fieldIndex, { value });
    },
    [objectIds, fieldIndex, updateFieldForMultipleObjects]
  );

  const editorType = getFieldEditorType(fieldInfo);
  const displayValue = editValue ?? (isMixed ? '' : commonValue ?? '');
  const placeholder = isMixed ? 'Multiple values' : fieldInfo.default || 'Enter value...';

  // Render different editors based on field type
  const renderEditor = () => {
    // For choice fields, use a select
    if (editorType === 'choice' && fieldInfo.choices && fieldInfo.choices.length > 0) {
      return (
        <select
          value={displayValue}
          onChange={(e) => {
            handleChange(e.target.value);
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {isMixed && <option value="">-- Multiple values --</option>}
          {!fieldInfo.required && <option value="">(not specified)</option>}
          {fieldInfo.choices.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      );
    }

    // For reference fields, use a select with objects
    if (editorType === 'reference' && referencedObjects.length > 0) {
      return (
        <select
          value={displayValue}
          onChange={(e) => {
            handleChange(e.target.value);
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {isMixed && <option value="">-- Multiple values --</option>}
          {!fieldInfo.required && <option value="">(none)</option>}
          {referencedObjects.map((obj) => {
            const objName = obj.fields[0]?.value || obj.className;
            return (
              <option key={obj.id} value={objName}>
                {objName} ({obj.className})
              </option>
            );
          })}
        </select>
      );
    }

    // For number fields
    if (editorType === 'number') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={displayValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            placeholder={placeholder}
            step={fieldInfo.type === 'integer' ? 1 : 0.01}
            min={fieldInfo.minimum}
            max={fieldInfo.maximum}
            className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {fieldInfo.units && (
            <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
              {fieldInfo.units}
            </span>
          )}
        </div>
      );
    }

    // For autosizable/autocalculatable fields
    if (editorType === 'autosizable' || editorType === 'autocalculatable') {
      const autoValue = editorType === 'autosizable' ? 'autosize' : 'autocalculate';
      const isAuto = displayValue.toLowerCase() === autoValue;

      return (
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isAuto}
              onChange={() => {
                handleChange(isAuto ? '' : autoValue);
              }}
              className="rounded border-input text-primary focus:ring-ring"
            />
            <span className="text-sm text-muted-foreground capitalize">{autoValue}</span>
          </label>
          {!isAuto && (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={displayValue}
                onChange={(e) => {
                  handleChange(e.target.value);
                }}
                placeholder={placeholder}
                step={fieldInfo.type === 'integer' ? 1 : 0.01}
                min={fieldInfo.minimum}
                max={fieldInfo.maximum}
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {fieldInfo.units && (
                <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                  {fieldInfo.units}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        value={displayValue}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
        placeholder={placeholder}
        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
      />
    );
  };

  return (
    <div
      className={`group relative rounded-md border p-3 transition-colors ${
        isMixed
          ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
          : 'border-border bg-background hover:border-muted-foreground/30'
      }`}
    >
      {/* Label row */}
      <div className="mb-1.5 flex items-center justify-between">
        <label className="flex items-center gap-1 text-sm font-medium text-foreground">
          {fieldInfo.name || fieldInfo.id}
          {fieldInfo.required && <span className="text-red-500 dark:text-red-400">*</span>}
        </label>
        {isMixed && (
          <button
            onClick={() => {
              setShowMixedDetails(!showMixedDetails);
            }}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
          >
            <MixedIcon className="h-3.5 w-3.5" />
            {uniqueValues.length} different values
          </button>
        )}
      </div>

      {/* Editor */}
      {renderEditor()}

      {/* Mixed values details */}
      {isMixed && showMixedDetails && (
        <div className="mt-2 rounded-md bg-amber-100/50 dark:bg-amber-900/20 p-2">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
            Current values across {objectIds.length} objects:
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {Array.from(valueCounts.entries()).map(([value, count]) => (
              <div
                key={value || '(empty)'}
                className="flex items-center justify-between text-xs text-amber-600 dark:text-amber-400"
              >
                <span className="truncate">{value || '(empty)'}</span>
                <span className="ml-2 rounded bg-amber-200 dark:bg-amber-800 px-1.5 py-0.5">
                  {count} object{count !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
            Editing will apply the same value to all selected objects.
          </p>
        </div>
      )}

      {/* Field metadata */}
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {fieldInfo.default !== undefined && <span>Default: {fieldInfo.default}</span>}
        {fieldInfo.units && <span className="font-medium">{fieldInfo.units}</span>}
        {(fieldInfo.minimum !== undefined || fieldInfo.maximum !== undefined) && (
          <span>
            Range: {fieldInfo.minimum ?? '-∞'} to {fieldInfo.maximum ?? '∞'}
          </span>
        )}
        {(fieldInfo.autosizable || fieldInfo.autocalculatable) && (
          <span className="rounded bg-blue-100 dark:bg-blue-900/30 px-1 text-blue-700 dark:text-blue-400">
            {fieldInfo.autosizable ? 'autosize' : 'autocalculate'}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Main Bulk Properties Panel Component
 */
export function BulkPropertiesPanel({ selectedObjectIds }: BulkPropertiesPanelProps) {
  const document = useIDFStore((state) => state.document);
  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);

  // Get all selected objects
  const selectedObjects = useMemo(() => {
    if (!document || selectedObjectIds.length === 0) return [];
    return document.objects.filter((obj) => selectedObjectIds.includes(obj.id));
  }, [document, selectedObjectIds]);

  // Check if all selected objects are of the same type
  const classNames = useMemo(() => {
    const names = new Set(selectedObjects.map((obj) => obj.className.toLowerCase()));
    return names;
  }, [selectedObjects]);

  const isSameType = classNames.size === 1;
  const commonClassName = isSameType ? selectedObjects[0]?.className : null;

  // Get class info from IDD
  const classInfo = useMemo(() => {
    if (!isSchemaLoaded || !commonClassName) return undefined;
    return schema.objectTypes.get(commonClassName.toLowerCase());
  }, [schema, commonClassName, isSchemaLoaded]);

  // Aggregate field values across all selected objects
  const aggregatedFields = useMemo((): AggregatedFieldState[] => {
    if (!classInfo || selectedObjects.length === 0) return [];

    const maxFields = Math.max(
      ...selectedObjects.map((obj) => obj.fields.length),
      classInfo.fields.length
    );

    const result: AggregatedFieldState[] = [];

    for (let i = 0; i < maxFields; i++) {
      const fieldInfo = classInfo.fields[i];
      if (!fieldInfo) continue;

      // Collect all values for this field
      const valueCounts = new Map<string, number>();
      for (const obj of selectedObjects) {
        const value = obj.fields[i]?.value ?? '';
        valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
      }

      const uniqueValues = Array.from(valueCounts.keys());
      const isMixed = uniqueValues.length > 1;
      const commonValue = isMixed ? undefined : uniqueValues[0];

      result.push({
        fieldIndex: i,
        fieldInfo,
        commonValue,
        isMixed,
        uniqueValues,
        valueCounts,
      });
    }

    return result;
  }, [classInfo, selectedObjects]);

  // Get objects that can be referenced for each field
  const getReferencedObjects = useCallback(
    (fieldInfo: IDDField): IDFObject[] => {
      if (!fieldInfo.objectList || !document || !isSchemaLoaded) return [];

      const referencedTypes = schema.referenceLists.get(fieldInfo.objectList);
      if (!referencedTypes) return [];

      return document.objects.filter((obj) => {
        return (
          referencedTypes.has(obj.className) || referencedTypes.has(obj.className.toLowerCase())
        );
      });
    },
    [document, schema, isSchemaLoaded]
  );

  // Separate required and optional fields
  const { requiredFields, optionalFields } = useMemo(() => {
    const required = aggregatedFields.filter((f) => f.fieldInfo.required);
    const optional = aggregatedFields.filter((f) => !f.fieldInfo.required);
    return { requiredFields: required, optionalFields: optional };
  }, [aggregatedFields]);

  // Count mixed fields
  const mixedFieldCount = useMemo(() => {
    return aggregatedFields.filter((f) => f.isMixed).length;
  }, [aggregatedFields]);

  // Show empty state if no objects selected
  if (selectedObjects.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-muted-foreground">
        <svg
          className="h-12 w-12 text-muted-foreground/50"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="mt-2 text-sm">Select objects to view properties</p>
      </div>
    );
  }

  // Show message if objects are of different types
  if (!isSameType) {
    return (
      <div className="p-4">
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-start gap-3">
            <svg
              className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
            </svg>
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-200">
                Multiple Object Types Selected
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Bulk editing is only available when all selected objects are of the same type.
              </p>
              <div className="mt-3">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
                  Selected types ({classNames.size}):
                </p>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {Array.from(classNames).map((name) => (
                    <span
                      key={name}
                      className="rounded bg-amber-100 dark:bg-amber-800 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-200"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Still show selection summary */}
        <div className="mt-4 rounded-lg border bg-card p-4">
          <h3 className="font-medium text-foreground">Selection Summary</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {selectedObjects.length} objects selected across {classNames.size} different types
          </p>
        </div>
      </div>
    );
  }

  // Show loading if schema not ready
  if (!classInfo) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-muted-foreground">
        <p className="text-sm">Loading schema...</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="mb-4 rounded-lg bg-primary/5 p-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-primary/10 p-2">
            <svg className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" />
            </svg>
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Bulk Edit</h2>
            <p className="text-sm text-muted-foreground">
              {selectedObjects.length} {commonClassName} objects selected
            </p>
          </div>
        </div>

        {/* Mixed fields indicator */}
        {mixedFieldCount > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded bg-amber-100 dark:bg-amber-900/30 px-2 py-1.5 text-xs text-amber-700 dark:text-amber-300">
            <MixedIcon className="h-4 w-4" />
            <span>
              {mixedFieldCount} field{mixedFieldCount !== 1 ? 's' : ''} with different values
            </span>
          </div>
        )}
      </div>

      {/* Class info */}
      {classInfo.memo && (
        <div className="mb-4 rounded-lg border bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">{classInfo.memo}</p>
        </div>
      )}

      {/* Fields */}
      <div className="space-y-3">
        {/* Required fields */}
        {requiredFields.length > 0 && (
          <>
            <FieldGroupSeparator label="Required" />
            {requiredFields.map((aggregatedField) => (
              <BulkFieldEditor
                key={aggregatedField.fieldIndex}
                aggregatedField={aggregatedField}
                objectIds={selectedObjectIds}
                referencedObjects={getReferencedObjects(aggregatedField.fieldInfo)}
              />
            ))}
          </>
        )}

        {/* Optional fields */}
        {optionalFields.length > 0 && (
          <>
            <FieldGroupSeparator label="Optional" />
            {optionalFields.map((aggregatedField) => (
              <BulkFieldEditor
                key={aggregatedField.fieldIndex}
                aggregatedField={aggregatedField}
                objectIds={selectedObjectIds}
                referencedObjects={getReferencedObjects(aggregatedField.fieldInfo)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
